import {
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
// Imported from `verifyClient` by its own path, NOT from the service barrel: the barrel re-exports
// `./verify`, which pulls `./canon` and `./diff` -- and with them the ENGINE_DERIVED_PATHS table --
// into whatever graph imports it. The surface is in the main bundle; the verifier's own
// canonicalisation belongs only in the separately emitted `basilVerify` chunk (ruling D2, gate G8).
import { createVerifyClient } from '../../../../services/EpisodeScriptVerifyService/verifyClient';
import type { VerifyClient } from '../../../../services/EpisodeScriptVerifyService/verifyClient';
import type {
  VerifierArtifactIdentity,
  VerifyWorkerFactory,
} from '../../../../services/EpisodeScriptVerifyService/workerProtocol';

/**
 * The client the surface holds while the real one is not up: available is false, the artifact is
 * null, and asking it for a verdict or a canon rejects. `useApply` reads `available` and refuses
 * with `verifier-unavailable`, which is the fail-closed half of ruling D2 — no verifier artifact,
 * no write, and no request of any kind issued.
 */
export const UNAVAILABLE_VERIFY_CLIENT: VerifyClient = {
  handshake: () => Promise.resolve(null),
  available: false,
  artifact: null,
  verify: () => Promise.reject(new Error("Basil's double-check isn't available right now, so changes are paused. You can still read and edit.")),
  canon: () => Promise.reject(new Error("Basil's double-check isn't available right now, so changes are paused. You can still read and edit.")),
  terminate: () => undefined,
};

/** Where the handshake has got to. `ready` is the ONLY state in which anything may be applied. */
export type VerifyClientStatus = 'idle' | 'connecting' | 'ready' | 'unavailable';

export interface VerifyClientHandle {
  /** The seam the write lane and the read lane both talk to. */
  client: VerifyClient;
  /**
   * True only after a handshake has ANSWERED. It is a value, not a getter on the client, because a
   * getter cannot re-render React: the surface needs the Apply control to enable at the moment the
   * artifact identifies itself, and a mutable field on a stable object never tells it that.
   */
  available: boolean;
  /** Who answered. Recorded in the audit entry before anything is written (BAR-SFR-01 point 1). */
  artifact: VerifierArtifactIdentity | null;
  status: VerifyClientStatus;
  /** Drop the worker and ask again. The only way out of `unavailable`. */
  retry: () => void;
}

export interface UseVerifyClientOptions {
  /**
   * The worker factory, by injection. Left out, the browser one is reached through a dynamic import
   * — `createVerifyWorker` carries `import.meta.url`, which jest's CommonJS runtime cannot parse
   * (U3 section 5.4), so NO test may put that module in its graph. Every test passes a factory here
   * instead, backed by an in-process handler.
   */
  factory?: VerifyWorkerFactory | null;
}

interface HandleState {
  client: VerifyClient;
  available: boolean;
  artifact: VerifierArtifactIdentity | null;
  status: VerifyClientStatus;
}

const IDLE_STATE: HandleState = {
  client: UNAVAILABLE_VERIFY_CLIENT, available: false, artifact: null, status: 'idle',
};

/**
 * The verifier seam, wired on mount and fail-closed.
 *
 * BAR-SHIP-01: the client used to be created and never asked to identify itself, so `available`
 * stayed false for the life of the modal and Apply could never enable. The handshake now happens
 * HERE, once, as soon as the modal is enabled, and its result is state — so the surface re-renders
 * when the artifact identifies itself, and only then.
 *
 * Until the handshake answers, and forever after one that failed unless the operator retries, the
 * surface holds {@link UNAVAILABLE_VERIFY_CLIENT} and nothing can be applied.
 */
export function useVerifyClient(enabled: boolean, options?: UseVerifyClientOptions): VerifyClientHandle {
  const [state, setState] = useState<HandleState>(IDLE_STATE);
  const [attempt, setAttempt] = useState(0);
  const factoryRef = useRef<VerifyWorkerFactory | null | undefined>(options ? options.factory : undefined);

  factoryRef.current = options ? options.factory : undefined;

  useEffect(() => {
    if (!enabled) {
      setState(IDLE_STATE);

      return undefined;
    }
    let live = true;
    let created: VerifyClient | null = null;

    setState({ ...IDLE_STATE, status: 'connecting' });

    const injected = factoryRef.current;
    const resolveFactory: Promise<VerifyWorkerFactory> = injected
      ? Promise.resolve(injected)
      : import('../../../../services/EpisodeScriptVerifyService/createVerifyWorker')
        .then((module) => module.createVerifyWorker);

    resolveFactory
      .then((factory) => {
        if (!live) {
          return undefined;
        }
        created = createVerifyClient(factory);

        return created.handshake().then((artifact) => {
          if (!live || !created) {
            return;
          }
          if (!artifact || !created.available) {
            setState({
              client: UNAVAILABLE_VERIFY_CLIENT, available: false, artifact: null, status: 'unavailable',
            });

            return;
          }
          setState({
            client: created, available: true, artifact, status: 'ready',
          });
        });
      })
      .catch(() => {
        if (live) {
          setState({
            client: UNAVAILABLE_VERIFY_CLIENT, available: false, artifact: null, status: 'unavailable',
          });
        }
      });

    return () => {
      live = false;
      if (created) {
        created.terminate();
      }
      setState(IDLE_STATE);
    };
  }, [attempt, enabled]);

  const retry = useCallback(() => {
    setAttempt((previous) => previous + 1);
  }, []);

  return {
    client: state.client,
    available: state.available,
    artifact: state.artifact,
    status: state.status,
    retry,
  };
}
